import { Lazy, Vector } from 'prelude-ts';
import Statement from './Statement';

/**
 * Description of a level in the game where the statement to be proven
 * is specified as a string for convenience. This string is automatically
 * parsed into formulae.
 */
interface LevelString {
  readonly name: string;
  readonly statement: string;
  readonly idealMoves: number;
  readonly draggingEnabled: boolean;
  readonly tooltip: boolean;
}

/**
 * Description of a level in the game.
 *
 * The statement to be proven is parsed lazily so as to not slowdown
 * startup. This statement is originally specified as a string for
 * developer convenience.
 */
export interface Level {
  readonly name: string;
  readonly statement: Lazy<Statement>;
  readonly idealMoves: number;
  readonly draggingEnabled: boolean;
  readonly tooltip: boolean;
}

/**
 * Ordered list of all levels in the game. The statement in each level
 * is specified as a string for developer convenience.
 */
const levelsString: Array<LevelString> = [
  {
    name: 'Axiom',
    statement: 'A => A',
    idealMoves: 1,
    draggingEnabled: false,
    tooltip: false
  },
  {
    name: 'Axioms',
    statement: 'A, B => B, A',
    idealMoves: 2,
    draggingEnabled: false,
    tooltip: false
  },

  // { name: 'Not', statement: 'A, -A =>', idealMoves: 2, draggingEnabled: false },
  // {
  //   name: 'Not Again',
  //   statement: '=> A, -A',
  //   idealMoves: 2,
  //   draggingEnabled: false
  // },

  {
    name: 'Tensor Left',
    statement: 'A * B => A, B',
    idealMoves: 3,
    draggingEnabled: false,
    tooltip: false
  },
  {
    name: 'Tensor Right',
    statement: 'B * A => A * B',
    idealMoves: 4,
    draggingEnabled: true,
    tooltip: true
  },
  {
    name: 'Tensors',
    statement: 'A * (B * C) => A * (B * C)',
    idealMoves: 7,
    draggingEnabled: true,
    tooltip: false
  },

  // { name: 'Black Hole', statement: '0 => A' },
  // { name: 'Black Hole Insanity', statement: 'A, B, C, 0 => A, B, C' },
  // { name: 'Black Hole Wat', statement: '0 =>' },

  {
    name: 'Plus',
    statement: 'A => A + B',
    idealMoves: 2,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'More Pluses',
    statement: 'A => (A + B) + A',
    idealMoves: 2,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'Plus Left',
    statement: 'A + B => A + B',
    idealMoves: 5,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'Plus Test',
    statement: 'A + B, A => A + B, A',
    idealMoves: 6,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'Distributivity',
    statement: '(A+B) + (B+C) => A+B+C',
    idealMoves: 13,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'Plus and Tensor',
    statement: 'A,B=> A * (A+B)',
    idealMoves: 4,
    draggingEnabled: true,
    tooltip: false
  },

  {
    name: 'Optional Left',
    statement: '1 * A => A',
    idealMoves: 3,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'Optional Right',
    statement: ' => C + \\bot',
    idealMoves: 2,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'Some Options',
    statement: '(A+(A*B)) => A*(B + \\bot)',
    idealMoves: 10,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'Lots of Options',
    statement: 'D,(A+1) => D * \\bot, (\\bot + A)',
    idealMoves: 9,
    draggingEnabled: true,
    tooltip: false
  },

  {
    name: 'Backwards Plus',
    statement: 'A&B=>A&B',
    idealMoves: 5,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'Pluses with choice',
    statement: 'A&B=>A+B',
    idealMoves: 3,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'Duality',
    statement: '((B&1)&A)=>A&B, \\bot',
    idealMoves: 7,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'mix 1',
    statement: '(B*1)+(B&(A&1)) => (\\bot * B)+(A+\\bot)',
    idealMoves: 9,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'par',
    statement: 'A||B => A,B',
    idealMoves: 3,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'par 2',
    statement: 'A||B => A||B',
    idealMoves: 4,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'par 3',
    statement: '(A||B)+A => (A||B)+(A+C)',
    idealMoves: 9,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'par 4',
    statement: '(C&(A*B))||((C&B)*A) => (A*B)||C',
    idealMoves: 9,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'mix 2',
    statement: '(C&B),(A*(A+B))=>(A+B)*(A*(B&C))',
    idealMoves: 14,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'double splitting',
    statement: '((C&A)&B)+(B&A) => (A*\\bot)&(B+(C&\\bot))',
    idealMoves: 15,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'mix 3',
    statement: 'A*(1&C) => (A*(C+B))&(\\bot*(C+A))',
    idealMoves: 13,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'par',
    statement: 'A||B => A,B',
    idealMoves: 3,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'par 2',
    statement: 'A||B => A||B',
    idealMoves: 4,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'par 3',
    statement: '(A||B)+A => (A||B)+(A+C)',
    idealMoves: 9,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'par 4',
    statement: '(C&(A*B))||((C&B)*A) => (A*B)||C',
    idealMoves: 9,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'par 5',
    statement: 'A*((A&B)||((A*B)&1)) => ((A||\\bot)*(B&A))+(A+B*\\bot)',
    idealMoves: 14,
    draggingEnabled: true,
    tooltip: false
  }
];

/**
 * Levels used in the study.
 */
const studyLevelsString: Array<LevelString> = [
  {
    name: 'Q1',
    statement: 'A * (B + C) => (A + B) * (A + C)',
    idealMoves: 12,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'Q2',
    statement: 'A => B',
    idealMoves: 0,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'Q3',
    statement: '(A + B), C => (B + A), C',
    idealMoves: 6,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'Q4',
    statement: 'A * 1 => \\top * A',
    idealMoves: 5,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'Q5',
    statement: 'A + B => A * B',
    idealMoves: 0,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'Q6',
    statement: 'A * B => A + B',
    idealMoves: 0,
    draggingEnabled: true,
    tooltip: false
  },
  {
    name: 'Q7',
    statement: 'A & B => A + B',
    idealMoves: 3,
    draggingEnabled: true,
    tooltip: false
  }
];

/**
 * Lazily parse a `LevelString` into a `Level`.
 *
 * @param levelString level specified as a string
 */
function parseLevel(levelString: LevelString): Level {
  return {
    name: levelString.name,
    statement: Lazy.of(() => Statement.parse(levelString.statement)),
    idealMoves: levelString.idealMoves,
    draggingEnabled: levelString.draggingEnabled,
    tooltip: levelString.tooltip
  };
}

/**
 * Ordered list of all levels in the game.
 */
export const levels: Vector<Level> = Vector.ofIterable(levelsString).map(
  parseLevel
);

/**
 * Ordered list of all levels used in the study.
 */
export const studyLevels: Vector<Level> = Vector.ofIterable(
  studyLevelsString
).map(parseLevel);
